import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ExternalLinkIcon } from 'lucide-react';
interface MeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const MEETING_URL = 'https://meetings-eu1.hubspot.com/philip-michael-steinke?uuid=3e26f099-b5de-4879-b5ef-f1f7d43292cd&embed=true';
const MEETING_DIRECT_URL = 'https://meetings-eu1.hubspot.com/philip-michael-steinke?uuid=3e26f099-b5de-4879-b5ef-f1f7d43292cd';
export function MeetingModal({
  isOpen,
  onClose
}: MeetingModalProps) {
  const [showHint, setShowHint] = useState(false);
  // Lock body scroll when modal is open (critical for iOS)
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isOpen]);
  // Show hint after 1 second, reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowHint(false);
      setInteractionCount(0);
      return;
    }
    const timer = setTimeout(() => setShowHint(true), 1000);
    return () => clearTimeout(timer);
  }, [isOpen]);
  // Hide hint after at least 2 interactions with the modal content
  const [interactionCount, setInteractionCount] = useState(0);
  const handleInteraction = useCallback(() => {
    if (showHint) {
      setInteractionCount(prev => {
        const next = prev + 1;
        if (next >= 2) {
          setShowHint(false);
        }
        return next;
      });
    }
  }, [showHint]);
  return <AnimatePresence data-id="element-66">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-8" data-id="element-67">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} data-id="element-68" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="relative w-full max-w-3xl bg-[#0A0A0F] border border-white/10 rounded-sm overflow-hidden flex flex-col" style={{
        height: '95dvh',
        maxHeight: '800px'
      }} onClick={handleInteraction} onTouchStart={handleInteraction} data-id="element-69">
            {/* Header */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-white/10 flex-shrink-0" data-id="element-70">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-muted" data-id="element-71">
                TERMIN BUCHEN
              </span>
              <button onClick={onClose} className="text-muted hover:text-white transition-colors duration-200 focus:outline-none p-1" aria-label="Schließen" data-id="element-72">
                <XIcon size={20} data-id="element-73" />
              </button>
            </div>

            {/* Fallback hint banner */}
            <AnimatePresence data-id="element-74">
              {showHint && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.3
          }} className="overflow-hidden flex-shrink-0 border-b border-white/10" data-id="element-75">
                  <div className="px-4 sm:px-6 py-2.5 bg-white/5 flex items-center justify-center gap-2 text-center" data-id="element-76">
                    <span className="text-xs sm:text-sm text-muted" data-id="element-77">
                      Kalender wird nicht angezeigt?
                    </span>
                    <a href={MEETING_DIRECT_URL} target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm text-lime font-semibold hover:underline inline-flex items-center gap-1" data-id="element-78">
                      Hier klicken
                      <ExternalLinkIcon size={12} data-id="element-79" />
                    </a>
                  </div>
                </motion.div>}
            </AnimatePresence>

            {/* Direct iframe to HubSpot Meetings */}
            <div className="flex-1 relative overflow-hidden" data-id="element-80">
              <iframe src={MEETING_URL} title="Termin vereinbaren" className="absolute inset-0 w-full h-full border-0" allow="camera; microphone" style={{
            background: '#ffffff'
          }} data-id="element-81" />
              {/* Cover bar to hide HubSpot branding at bottom */}
              <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{
            height: '60px',
            background: 'linear-gradient(to top, #0A0A0F 70%, transparent)',
            zIndex: 10
          }} aria-hidden="true" data-id="element-82" />
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}